/**
 * IndexedDB retry queue for POS transactions that could not reach the API.
 * Each entry keeps its original Idempotency-Key, so a retry after a lost response
 * is replayed by the server instead of being booked twice.
 */
import type { PosTransactionRequest, PosTransactionResult } from "./api-types";
import { ApiHttpError } from "./staff-api-client";

export interface QueuedTransaction {
  idempotencyKey: string;
  request: PosTransactionRequest;
  createdAt: string;
  attempts: number;
  /** `failed` = the server rejected it (4xx); it stays visible until staff remove it. */
  status: "pending" | "failed";
  lastError?: string;
}

const DB_NAME = "gp-scanner";
const STORE = "transactions";

let dbPromise: Promise<IDBDatabase> | null = null;
const listeners = new Set<() => void>();
let draining: Promise<void> | null = null;

function openDb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    const open = indexedDB.open(DB_NAME, 1);
    open.onupgradeneeded = () => {
      open.result.createObjectStore(STORE, { keyPath: "idempotencyKey" });
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error ?? new Error("IndexedDB open failed"));
    open.onblocked = () => reject(new Error("IndexedDB open blocked"));
  }).catch((error: unknown) => {
    dbPromise = null;
    throw error;
  });
  return dbPromise;
}

async function withStore<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = run(tx.objectStore(STORE));
    tx.oncomplete = () => resolve(req.result);
    tx.onerror = () => reject(tx.error ?? req.error ?? new Error("IndexedDB transaction failed"));
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB transaction aborted"));
  });
}

function notify(): void {
  for (const listener of listeners) listener();
}

/** Called whenever the queue changes; returns an unsubscribe function. */
export function onQueueChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export async function listQueued(): Promise<QueuedTransaction[]> {
  const all = await withStore<QueuedTransaction[]>("readonly", (s) => s.getAll() as IDBRequest<QueuedTransaction[]>);
  return all.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function enqueueTransaction(idempotencyKey: string, request: PosTransactionRequest): Promise<void> {
  const entry: QueuedTransaction = { idempotencyKey, request, createdAt: new Date().toISOString(), attempts: 0, status: "pending" };
  await withStore("readwrite", (s) => s.put(entry));
  notify();
}

export async function removeQueued(idempotencyKey: string): Promise<void> {
  await withStore("readwrite", (s) => s.delete(idempotencyKey));
  notify();
}

async function put(entry: QueuedTransaction): Promise<void> {
  await withStore("readwrite", (s) => s.put(entry));
}

export type SendTransaction = (request: PosTransactionRequest, idempotencyKey: string) => Promise<PosTransactionResult>;

/**
 * Sends pending entries oldest first. Stops at the first network/5xx failure (still offline);
 * marks 4xx rejections as failed. Concurrent calls share one run.
 */
export function drainQueue(send: SendTransaction): Promise<void> {
  draining ??= (async () => {
    try {
      for (const entry of await listQueued()) {
        if (entry.status !== "pending") continue;
        entry.attempts += 1;
        try {
          await send(entry.request, entry.idempotencyKey);
          await withStore("readwrite", (s) => s.delete(entry.idempotencyKey));
        } catch (error) {
          if (error instanceof ApiHttpError && !error.retryable) {
            entry.status = "failed";
            entry.lastError = `${error.status} ${error.body.code}`;
            await put(entry);
            continue;
          }
          entry.lastError = error instanceof Error ? error.message : String(error);
          await put(entry);
          break;
        }
      }
    } finally {
      draining = null;
      notify();
    }
  })();
  return draining;
}
